import VueDatePicker from "@vuepic/vue-datepicker";
import { createApp } from "vue";
import ToastPlugin from "vue-toast-notification";
import App from "./App.vue";
import validationModule from "./plugins/validationModule";
import vuetify from "./plugins/vuetify";
import router from "./router";
import store from "./store";
import "@vuepic/vue-datepicker/dist/main.css";

import "typeface-roboto/index.css";
import "@mdi/font/css/materialdesignicons.css";
import "vue-toast-notification/dist/theme-bootstrap.css";

import { ApiClient } from "@/services/apiClient";
import { refreshAccessToken, setAccessToken } from "@/services/authService";
import batchClient from "@/services/batchClient";
import cacheServiceClient from "@/services/cacheServiceClient";
import { markSessionReady } from "@/services/session";
import supervisionClient from "@/services/supervisionClient";
import validationRulesService from "@/services/validationRulesService";
import { AUTH_KEYCLOAK_INIT, AUTH_LOGOUT } from "@/store/actions/auth";
import { batchProgress, useWebSocketHandler } from "@/utils/websocket";

const plugins = [store, router, vuetify, ToastPlugin, validationModule];

async function bootstrap() {
	const unAuth = () => store.dispatch(AUTH_LOGOUT);

	const app = createApp(App);
	app.component("VueDatePicker", VueDatePicker);
	app.config.globalProperties.$apiClient = new ApiClient(unAuth);
	app.config.globalProperties.$supervisionClient = supervisionClient;
	app.config.globalProperties.$batchClient = batchClient;
	app.config.globalProperties.$cacheServiceClient = cacheServiceClient;
	app.config.globalProperties.$validationRulesService = validationRulesService;

	plugins.forEach((plugin) => {
		app.use(plugin);
	});

	app.config.globalProperties.$showNotification = function (message, type) {
		this.$toast.open({
			position: "bottom",
			message: message,
			type: type,
			duration: 6000,
		});
	};

	if ("serviceWorker" in navigator) {
		window.addEventListener("load", () => {
			navigator.serviceWorker
				.register("/service-worker.js")
				.then((registration) => {
					console.log(
						"ServiceWorker registration successful with scope: ",
						registration.scope,
					);
				})
				.catch((err) => {
					console.log("ServiceWorker registration failed: ", err);
				});
		});
	}

	// Auth state is no longer persisted, but a previous build may have left a
	// stale state.auth.token in localStorage. Reset before attempting refresh
	// so isAuthenticated reflects the in-memory access token, not stale state.
	store.commit(AUTH_LOGOUT);

	// Try to restore session before mounting (with short timeout)
	try {
		const token = await Promise.race([
			refreshAccessToken(),
			new Promise((_, reject) =>
				setTimeout(() => reject(new Error("timeout")), 5000),
			),
		]);
		if (token) {
			setAccessToken(token);
			await store.dispatch(AUTH_KEYCLOAK_INIT);
		}
	} catch {
		// No valid session — auth state already cleared above, router guard
		// will redirect to /login.
	}
	markSessionReady();

	// $batchProgress points at the shared reactive inside websocket.js, so
	// components bind their reactivity once and pick up updates whenever the
	// handler (re)initializes.
	app.config.globalProperties.$batchProgress = batchProgress;

	// Bootstrap runs once per page load, but after a Keycloak redirect the
	// token arrives later via CallbackView → AUTH_KEYCLOAK_INIT (SPA nav, no
	// reload), so isAuthenticated flips false → true after this point. Init
	// up front if we already have a session (full reload case) and watch for
	// the flip in the callback case.
	let wsInitialized = false;
	const ensureWs = () => {
		if (wsInitialized) return;
		if (!store.getters.isAuthenticated) return;
		useWebSocketHandler();
		wsInitialized = true;
	};

	ensureWs();
	store.watch(
		(_state, getters) => getters.isAuthenticated,
		(isAuthenticated) => {
			if (isAuthenticated) ensureWs();
		},
	);

	app.mount("#app");
}

bootstrap().catch((err) => {
	console.error("App bootstrap failed:", err);
	markSessionReady();
	document.body.innerHTML =
		'<div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><h2>Application failed to start. Please try again later.</h2></div>';
});
